import {useState} from 'react';
import {AwAppShell, AwCheckbox, AwErrorState, AwLoadingState, AwPageHeader, AwPanel, AwEmptyState} from '@ascension-tracker/ascension-ui';
import {useMyTasks, useToggleMyTaskComplete, type MyTask} from '../hooks/useTasks.js';
import {TaskDetailPanel} from '../components/TaskDetailPanel.js';

function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function bucketOf(task: MyTask, today: string): 'overdue' | 'today' | 'upcoming' | 'noDate' {
  if (!task.dueOn) return 'noDate';
  const dueKey = task.dueOn.slice(0, 10);
  if (dueKey < today) return 'overdue';
  if (dueKey === today) return 'today';
  return 'upcoming';
}

const bucketOrder: Array<{id: ReturnType<typeof bucketOf>; label: string}> = [
  {id: 'overdue', label: 'Overdue'},
  {id: 'today', label: 'Do Today'},
  {id: 'upcoming', label: 'Upcoming'},
  {id: 'noDate', label: 'No Due Date'}
];

export function MyTasks() {
  const tasks = useMyTasks();
  const toggleComplete = useToggleMyTaskComplete();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const today = todayKey();
  const buckets = new Map<string, MyTask[]>();
  for (const task of tasks.data ?? []) {
    const bucket = bucketOf(task, today);
    buckets.set(bucket, [...(buckets.get(bucket) ?? []), task]);
  }

  return (
    <AwAppShell>
      <AwPageHeader eyebrow="Personal Workspace" title="My Tasks">
        <p>Everything assigned to you across every project, grouped by due date.</p>
      </AwPageHeader>
      {tasks.error && <AwErrorState>{(tasks.error as Error).message}</AwErrorState>}
      {!tasks.data ? <AwLoadingState/> : tasks.data.length === 0 ? (
        <AwPanel><AwEmptyState title="Nothing assigned to you">Tasks assigned to you across all projects will show up here.</AwEmptyState></AwPanel>
      ) : (
        <div className="my-tasks">
          {bucketOrder.map(bucket => {
            const bucketTasks = buckets.get(bucket.id) ?? [];
            if (bucketTasks.length === 0) return null;
            return (
              <AwPanel key={bucket.id} className={`my-tasks__bucket my-tasks__bucket--${bucket.id}`}>
                <header><h2>{bucket.label}</h2><span>{bucketTasks.length}</span></header>
                {bucketTasks.map(task => (
                  <div key={task.id} className="my-tasks__row">
                    <AwCheckbox id={`my-task-complete-${task.id}`} label="" checked={Boolean(task.completedAt)} onChange={() => toggleComplete.mutate(task)}/>
                    <button type="button" className="my-tasks__title" onClick={() => setSelectedTaskId(task.id)}>{task.title}</button>
                    <a className="my-tasks__project" href={`/projects/${task.projectKey}/tasks`}>{task.projectName}</a>
                  </div>
                ))}
              </AwPanel>
            );
          })}
        </div>
      )}
      <TaskDetailPanel taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)}/>
    </AwAppShell>
  );
}
